<template>
  <div class="withdraw-step" data-testid="withdraw-step">
    <section class="withdraw-step__header">
      <Row
        class="withdraw-step__header--row"
        :has-line="false"
        chevron
        @click="openModal"
      >
        <template #top-left>
          <BaseText size="p4" :color="NebraskaColors.elementSecondary">
            Disponível para resgate
          </BaseText>
          <BaseText size="p4" :color="NebraskaColors.elementPrimary">
            {{ withdrawDetails.availableToRedemption.formatted }}
          </BaseText>
        </template>
        <template #top-right>
          <BaseText size="p4" :color="NebraskaColors.elementPrimary">
            Entenda o valor
          </BaseText>
        </template>
      </Row>
    </section>
    <main class="withdraw-step__main">
      <HeadingDisplay
        class="withdraw-step__title"
        title="Defina o valor"
        :subtitle="withdrawDetails.name"
      />
      <InputMoney
        class="withdraw-step__input"
        :shortcut-value="shortcuts"
        :status="helperText ? 'error' : ''"
        :value="withdrawInput.formatted"
        :loading="isLoadingAmount"
        :helper-text="helperText"
        :show-buttons="showButtons"
        :increment="incrementValue"
        :minimum="minimumWithdraw"
        :update-value="updateValue"
        @blur="handleBlur"
        @change="debounceChangeValue"
      />
      <div class="withdraw-step__list">
        <ListDisplay
          v-for="(detail, index) in details"
          :key="index"
          class="withdraw-step__list--item"
          :has-divider="index !== details.length - 1"
          :title="detail.label"
          :value="detail.value"
        >
          <template v-if="detail.loading" #value-slot>
            <SkeletonLoader show-animation>
              <div class="withdraw-step__list--loader" />
            </SkeletonLoader>
          </template>
        </ListDisplay>
      </div>
    </main>
    <ButtonContainer class="withdraw-step__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          size="large"
          :disabled="isDisabled"
          :loading="isLoadingAmount || isFetchingReview"
          @click="handleClick"
        />
      </template>
    </ButtonContainer>

    <ModalAvailableForRedemption
      :is-open="modalIsOpen"
      :withdraw-details="withdrawDetails"
      @close="closeModal"
    />
  </div>
</template>

<script lang="ts" setup>
import { computed, inject, ref, Ref } from 'vue';
import { useRoute } from 'vue-router/composables';

import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import {
  BaseText,
  Row,
  HeadingDisplay,
  ListDisplay,
  SkeletonLoader,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { InputMoney } from '@/modules/transaction/components/InputMoney';

import {
  getWithdrawAmount,
  IWithdrawDetailsV2
} from '@/modules/transaction/services/product-withdraw';

import { errorText, listDetails, EProductTypes } from './helpers';
import { debounce } from 'lodash-es';
import ModalAvailableForRedemption from './components/ModalAvailableForRedemption/ModalAvailableForRedemption.vue';

const route = useRoute();

const withdrawDetails = inject('withdrawDetailsV2') as Ref<IWithdrawDetailsV2>;
const isFetchingReview = inject('isFetchingReview') as Ref<boolean>;
const productId = inject('productId') as Ref<string>;

const fetchWithdrawReview = inject('fetchWithdrawReview', (amount: number) => ({
  amount
}));

const updateValue = ref(false);
const isLoadingAmount = ref(false);
const netBalance = ref('R$ 0,00');
const isButtonDisabled = ref(false);
const modalIsOpen = ref(false);

const withdrawInput = ref({
  value: 0,
  formatted: ''
});

const productType = computed(() => {
  const { type } = route.params;
  return type;
});

const helperText = computed(() => {
  return errorText(
    withdrawDetails.value,
    withdrawInput.value.value,
    productType.value
  );
});

const details = computed(() => {
  return listDetails(
    withdrawDetails.value,
    netBalance.value,
    productType.value,
    isLoadingAmount.value
  );
});

const showButtons = computed(() => {
  return productType.value !== EProductTypes.FUNDS;
});

const shortcuts = computed(() => {
  return productType.value === EProductTypes.FUNDS
    ? withdrawDetails.value.buttons
    : [];
});

const incrementValue = computed((): number => {
  return withdrawDetails.value.increment.value;
});

const minimumWithdraw = computed(() => {
  return withdrawDetails.value.minimumRedemption.value;
});

const totalBalance = computed(() => {
  return withdrawDetails.value.availableToRedemption.value;
});

const isDisabled = computed(() => {
  return (
    withdrawInput.value.value <= 0 ||
    helperText.value !== '' ||
    isButtonDisabled.value
  );
});

const debounceChangeValue = debounce(async data => {
  onChangeValue(data);
}, 500);

function onChangeValue(data: {
  value: number;
  formatted: string;
  increment: boolean;
}) {
  withdrawInput.value = data;
  netBalance.value = 'R$ 0,00';
  isButtonDisabled.value = true;

  if (data.increment) {
    const isTotal = data.value === totalBalance.value;
    const meetsMinimum = data.value >= minimumWithdraw.value;

    if (isTotal || meetsMinimum) {
      fetchWithdrawAmount(data.value);
    }
  }
}

function handleClick() {
  fetchWithdrawReview(withdrawInput.value.value);
}

function handleBlur() {
  const value = withdrawInput.value.value;
  const isTotal = value === totalBalance.value;

  if (value < minimumWithdraw.value && !isTotal) {
    netBalance.value = 'R$ 0,00';
    return;
  }

  fetchWithdrawAmount(value);
}

async function fetchWithdrawAmount(amount: number) {
  isLoadingAmount.value = true;

  try {
    const data = await getWithdrawAmount(productId.value, amount);

    netBalance.value = data.netBalance.formatted;

    isButtonDisabled.value = false;
  } catch (e) {
    console.error(e);
  } finally {
    isLoadingAmount.value = false;
  }
}

function openModal() {
  modalIsOpen.value = true;
}

function closeModal() {
  modalIsOpen.value = false;
}
</script>

<style lang="less" scoped>
.width() {
  max-width: 492px;
  width: 100%;
}

:deep(.wrapper) {
  align-items: center;
}

.withdraw-step {
  &__header {
    background-color: @background-tertiary;
    height: 76px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 @size-spacing-x500;

    &--row {
      .width();
      background-color: transparent;
      padding: 0;
      display: flex;
      align-items: center;

      &:hover {
        cursor: pointer;
      }
    }
  }

  &__main {
    padding: @size-spacing-x1000 @size-spacing-x500 @size-spacing-x1800;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  &__title {
    .width();
    margin-bottom: @size-spacing-x600;
  }

  &__input {
    .width();
    margin-bottom: @size-spacing-x800;
  }

  &__list {
    .width();

    &--item {
      margin-bottom: @size-spacing-x350;
    }

    &--loader {
      height: 26px;
      width: 50px;
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
